import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { SiteLayout } from "@/components/site/SiteLayout";
import { supabase } from "@/integrations/supabase/client";
import { generateNow, getAdminData, resumeJob, saveSettings, setPostStatus } from "@/lib/admin.functions";
import { getAdClicks } from "@/lib/ads.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Painel administrativo | Chavel Chaveiro Betim" },
      { name: "description", content: "Gestão de leads, conteúdo de IA e tags de rastreamento da Chavel." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Painel administrativo | Chavel" },
      { property: "og:description", content: "Área interna da Chavel Chaveiro." },
    ],
  }),
  component: AdminPage,
});

type FormState = {
  gtm_id: string;
  ga4_id: string;
  google_ads_id: string;
  clarity_id: string;
  zapier_webhook_url: string;
  auto_publish: boolean;
};

const emptyForm: FormState = {
  gtm_id: "",
  ga4_id: "",
  google_ads_id: "",
  clarity_id: "",
  zapier_webhook_url: "",
  auto_publish: false,
};

function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-data"],
    queryFn: () => getAdminData(),
    retry: false,
  });

  const adClicks = useQuery({
    queryKey: ["admin-ad-clicks"],
    queryFn: () => getAdClicks(),
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      gtm_id: data.settings?.gtm_id ?? "",
      ga4_id: data.settings?.ga4_id ?? "",
      google_ads_id: data.settings?.google_ads_id ?? "",
      clarity_id: data.settings?.clarity_id ?? "",
      zapier_webhook_url: data.privateSettings?.zapier_webhook_url ?? "",
      auto_publish: data.privateSettings?.auto_publish ?? false,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      saveSettings({
        data: {
          gtm_id: form.gtm_id || null,
          ga4_id: form.ga4_id || null,
          google_ads_id: form.google_ads_id || null,
          clarity_id: form.clarity_id || null,
          zapier_webhook_url: form.zapier_webhook_url || null,
          auto_publish: form.auto_publish,
        },
      }),
    onSuccess: () => {
      toast.success("Configurações salvas");
      void queryClient.invalidateQueries({ queryKey: ["admin-data"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const generate = useMutation({
    mutationFn: () => generateNow(),
    onSuccess: (result) => {
      toast.success(`Job: ${result.status}${result.detail ? ` — ${result.detail}` : ""}`);
      void queryClient.invalidateQueries({ queryKey: ["admin-data"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggleStatus = useMutation({
    mutationFn: (input: { id: string; status: "draft" | "published" }) => setPostStatus({ data: input }),
    onSuccess: () => {
      toast.success("Status atualizado");
      void queryClient.invalidateQueries({ queryKey: ["admin-data"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resume = useMutation({
    mutationFn: () => resumeJob(),
    onSuccess: () => {
      toast.success("Job reativado");
      void queryClient.invalidateQueries({ queryKey: ["admin-data"] });
    },
  });

  if (isLoading) {
    return (
      <SiteLayout>
        <div className="container-page py-16 text-sm text-muted-foreground">Carregando painel…</div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <div className="container-page py-16">
          <h1 className="font-display text-2xl">Acesso restrito</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sua conta ainda não tem permissão de administrador. Peça para um administrador liberar o seu acesso.
          </p>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="container-page space-y-10 py-12">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl">Painel Chavel</h1>
            <p className="text-sm text-muted-foreground">Rastreamento, leads e conteúdo automático.</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              await supabase.auth.signOut();
              void navigate({ to: "/auth" });
            }}
            className="rounded-full border border-border px-4 py-2 text-sm"
          >
            Sair
          </button>
        </header>

        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-xl">Tags de rastreamento</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Cole os IDs quando tiver as contas criadas. Os scripts só são carregados quando preenchidos.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Google Tag Manager (GTM-XXXXXX)" value={form.gtm_id} onChange={(v) => setForm({ ...form, gtm_id: v })} />
            <Field label="Google Analytics 4 (G-XXXXXXX)" value={form.ga4_id} onChange={(v) => setForm({ ...form, ga4_id: v })} />
            <Field label="Google Ads (AW-XXXXXXX)" value={form.google_ads_id} onChange={(v) => setForm({ ...form, google_ads_id: v })} />
            <Field label="Microsoft Clarity" value={form.clarity_id} onChange={(v) => setForm({ ...form, clarity_id: v })} />
            <Field
              label="Webhook do Zapier (GBP + Instagram)"
              value={form.zapier_webhook_url}
              onChange={(v) => setForm({ ...form, zapier_webhook_url: v })}
            />
            <label className="flex items-end gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.auto_publish}
                onChange={(event) => setForm({ ...form, auto_publish: event.target.checked })}
              />
              Publicar e enviar ao Zapier automaticamente
            </label>
          </div>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="mt-5 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {save.isPending ? "Salvando…" : "Salvar configurações"}
          </button>
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl">Conteúdo automático por IA</h2>
              <p className="text-sm text-muted-foreground">
                Agendado 2x por semana (terça e sexta). Última execução:{" "}
                {data?.job?.last_run_at ? new Date(data.job.last_run_at).toLocaleString("pt-BR") : "nunca"}.
                {data?.job?.paused ? ` Pausado: ${data.job.paused_reason}` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              {data?.job?.paused ? (
                <button type="button" onClick={() => resume.mutate()} className="rounded-full border border-border px-4 py-2 text-sm">
                  Reativar job
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="rounded-full bg-gold px-5 py-2 text-sm font-semibold text-gold-foreground disabled:opacity-60"
              >
                {generate.isPending ? "Gerando…" : "Gerar artigo agora"}
              </button>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Título</th>
                  <th>Palavra-chave</th>
                  <th>Instagram</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data?.posts.map((post) => (
                  <tr key={post.id} className="border-t border-border">
                    <td className="py-2 pr-3">{post.title}</td>
                    <td className="pr-3 text-muted-foreground">{post.target_keyword}</td>
                    <td className="pr-3 text-muted-foreground">{post.instagram_format}</td>
                    <td className="pr-3">{post.status === "published" ? "Publicado" : "Rascunho"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          toggleStatus.mutate({
                            id: post.id,
                            status: post.status === "published" ? "draft" : "published",
                          })
                        }
                        className="text-xs font-semibold text-primary underline"
                      >
                        {post.status === "published" ? "Despublicar" : "Publicar"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!data?.posts.length ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-muted-foreground">Nenhum artigo gerado ainda.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl">Cliques de anúncios e IPs</h2>
              <p className="text-sm text-muted-foreground">
                Cada acesso vindo do Google Ads é registrado com o IP. IPs com 3 ou mais cliques podem ser
                concorrentes — bloqueie-os em Campanha › Configurações › Exclusões de IP.
              </p>
            </div>
            {adClicks.data?.suspicious.length ? (
              <button
                type="button"
                onClick={async () => {
                  const list = adClicks.data.suspicious.map((row) => row.ip).join("\n");
                  try {
                    await navigator.clipboard.writeText(list);
                    toast.success("IPs suspeitos copiados");
                  } catch {
                    toast.error("Não foi possível copiar. Selecione os IPs na tabela.");
                  }
                }}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold"
              >
                Copiar IPs suspeitos
              </button>
            ) : null}
          </div>

          {adClicks.isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Carregando cliques…</p>
          ) : adClicks.error ? (
            <p className="mt-4 text-sm text-muted-foreground">Não foi possível carregar os cliques agora.</p>
          ) : (
            <>
              {adClicks.data?.suspicious.length ? (
                <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4">
                  <h3 className="text-sm font-semibold">IPs repetidos (possíveis concorrentes)</h3>
                  <ul className="mt-2 space-y-1 text-sm">
                    {adClicks.data.suspicious.map((row) => (
                      <li key={row.ip} className="flex flex-wrap gap-2">
                        <span className="font-mono font-semibold">{row.ip}</span>
                        <span className="text-muted-foreground">
                          {row.count} cliques · último em {new Date(row.lastAt).toLocaleString("pt-BR")}
                          {row.terms.length ? ` · termos: ${row.terms.join(", ")}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-2">Data</th>
                      <th>IP</th>
                      <th>Campanha</th>
                      <th>Termo</th>
                      <th>Página</th>
                      <th>Local</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adClicks.data?.clicks.map((click) => (
                      <tr key={click.id} className="border-t border-border">
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {new Date(click.created_at).toLocaleString("pt-BR")}
                        </td>
                        <td className="pr-3 font-mono">{click.ip ?? "—"}</td>
                        <td className="pr-3">{click.utm_campaign ?? click.utm_source ?? "—"}</td>
                        <td className="pr-3">{click.utm_term ?? "—"}</td>
                        <td className="pr-3 text-muted-foreground">{click.landing_page ?? "—"}</td>
                        <td className="text-muted-foreground">
                          {[click.city, click.country].filter(Boolean).join(" / ") || "—"}
                        </td>
                      </tr>
                    ))}
                    {!adClicks.data?.clicks.length ? (
                      <tr>
                        <td colSpan={6} className="py-4 text-muted-foreground">
                          Nenhum clique de anúncio registrado ainda.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>


        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-xl">Leads do WhatsApp</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Data</th>
                  <th>Nome</th>
                  <th>Serviço</th>
                  <th>Local</th>
                  <th>Página</th>
                </tr>
              </thead>
              <tbody>
                {data?.leads.map((lead) => (
                  <tr key={lead.id} className="border-t border-border">
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(lead.created_at).toLocaleString("pt-BR")}</td>
                    <td className="pr-3">{lead.name ?? "—"}</td>
                    <td className="pr-3">{lead.service}</td>
                    <td className="pr-3">{lead.location}</td>
                    <td className="text-muted-foreground">{lead.source_page ?? "—"}</td>
                  </tr>
                ))}
                {!data?.leads.length ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-muted-foreground">Nenhum lead registrado ainda.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-sm">
      <span className="font-medium">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
